import { t } from "i18next";
import { z } from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  longTextSchema,
  valueAndLabelSchemaDeprecated,
  yesNoSchema,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";

export const formSpecification = new MultiPageFormBuilder()
  .withFieldAlwaysVisible({
    fieldName: "incidentDescription", // 136-healthinfo-1.0
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    label: tk("informationForHealthcare.incidentDescription"),
    requiredLabel: tk("informationForHealthcare.incidentDescriptionRequired"),
    placeholder: tk("informationForHealthcare.incidentDescriptionPlaceholder"),
    multiline: true,
    rows: "5",
  })
  .withFieldAlwaysVisible({
    fieldName: "wasHealthcareProvided", // 136-healthinfo-2.0
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("informationForHealthcare.wasHealthcareProvided"),
    requiredLabel: tk("informationForHealthcare.wasHealthcareProvidedRequired"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "medicalAttentionProvided", // 136-healthinfo-2.1
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.wasHealthcareProvided?.value === "yes",
    label: tk("informationForHealthcare.medicalAttentionProvided"),
    requiredLabel: tk(
      "informationForHealthcare.medicalAttentionProvidedRequired",
    ),
    multiline: true,
    rows: "5",
  })
  .withFieldAlwaysVisible({
    fieldName: "wasForceUsed", // 136-healthinfo-3.0
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("informationForHealthcare.wasForceUsed"),
    requiredLabel: tk("informationForHealthcare.wasForceUsedRequired"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withReadonlyFieldAlwaysVisible({
    fieldName: "useOfForceReminderBanner", // 136-healthinfo-3.1
    type: "banner",
    bannerType: "info",
    body: tk("informationForHealthcare.useOfForceReminder"),
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceType", // 136-healthinfo-3.2
    type: "multi-select",
    schema: z
      .array(valueAndLabelSchemaDeprecated(z.string()))
      .min(1, "At least one option must be selected."),
    isVisible: ({ formValues }) => formValues.wasForceUsed?.value === "yes",
    label: tk("informationForHealthcare.useOfForceType.title"),
    requiredLabel: tk("informationForHealthcare.useOfForceType.required"),
    options: [
      {
        value: "handcuffing",
        label: tk(
          "informationForHealthcare.useOfForceType.options.handcuffing",
        ),
      },
      {
        value: "unarmedSkills",
        label: tk(
          "informationForHealthcare.useOfForceType.options.unarmedSkills",
        ),
      },
      {
        value: "dogs",
        label: tk("informationForHealthcare.useOfForceType.options.dogs"),
      },
      {
        value: "baton",
        label: tk("informationForHealthcare.useOfForceType.options.baton"),
      },
      {
        value: "irritantSpray",
        label: tk(
          "informationForHealthcare.useOfForceType.options.irritantSpray",
        ),
      },
      {
        value: "limbBodyRestraints",
        label: tk(
          "informationForHealthcare.useOfForceType.options.limbBodyRestraints",
        ),
      },
      {
        value: "spitGuard",
        label: tk("informationForHealthcare.useOfForceType.options.spitGuard"),
      },
      {
        value: "shield",
        label: tk("informationForHealthcare.useOfForceType.options.shield"),
      },
      {
        value: "ced",
        label: tk("informationForHealthcare.useOfForceType.options.ced"),
      },
      {
        value: "aep",
        label: tk("informationForHealthcare.useOfForceType.options.aep"),
      },
      {
        value: "firearms",
        label: tk("informationForHealthcare.useOfForceType.options.firearms"),
      },
      {
        value: "otherImprovised",
        label: tk(
          "informationForHealthcare.useOfForceType.options.otherImprovised",
        ),
      },
    ],
  })
  // Handcuffing context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextHandcuffing", // 136-healthinfo-3.3
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(
        ({ value }) => value === "handcuffing",
      ) !== undefined,
    label: tk("informationForHealthcare.useOfForceContextHandcuffing.title"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextHandcuffing.required",
    ),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextHandcuffingOther", // 136-healthinfo-3.15
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextHandcuffing?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // Unarmed skills context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextUnarmedSkills", // 136-healthinfo-3.4
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(
        ({ value }) => value === "unarmedSkills",
      ) !== undefined,
    label: tk("informationForHealthcare.useOfForceContextUnarmedSkills.title"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextUnarmedSkills.required",
    ),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextUnarmedSkillsOther", // 136-healthinfo-3.16
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextUnarmedSkills?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // Dogs context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextDogs", // 136-healthinfo-3.5
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(({ value }) => value === "dogs") !==
      undefined,
    label: tk("informationForHealthcare.useOfForceContextDogs.title"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextDogs.required",
    ),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextDogsOther", // 136-healthinfo-3.17
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextDogs?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // Baton context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextBaton", // 136-healthinfo-3.6
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(({ value }) => value === "baton") !==
      undefined,
    label: tk("informationForHealthcare.useOfForceContextBaton.title"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextBaton.required",
    ),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextBatonOther", // 136-healthinfo-3.18
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextBaton?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // Irritant spray context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextIrritantSpray", // 136-healthinfo-3.7
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(
        ({ value }) => value === "irritantSpray",
      ) !== undefined,
    label: tk("informationForHealthcare.useOfForceContextIrritantSpray.title"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextIrritantSpray.required",
    ),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextIrritantSprayOther", // 136-healthinfo-3.19
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextIrritantSpray?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // Limb/body restraints context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextLimbBodyRestraints", // 136-healthinfo-3.8
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(
        ({ value }) => value === "limbBodyRestraints",
      ) !== undefined,
    label: tk(
      "informationForHealthcare.useOfForceContextLimbBodyRestraints.title",
    ),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextLimbBodyRestraints.required",
    ),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextLimbBodyRestraintsOther", // 136-healthinfo-3.20
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextLimbBodyRestraints?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // Spit guard context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextSpitGuard", // 136-healthinfo-3.9
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(({ value }) => value === "spitGuard") !==
      undefined,
    label: tk("informationForHealthcare.useOfForceContextSpitGuard.title"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextSpitGuard.required",
    ),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextSpitGuardOther", // 136-healthinfo-3.21
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextSpitGuard?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // Shield context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextShield", // 136-healthinfo-3.10
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(({ value }) => value === "shield") !==
      undefined,
    label: tk("informationForHealthcare.useOfForceContextShield.title"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextShield.required",
    ),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextShieldOther", // 136-healthinfo-3.22
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextShield?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // CED context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextCed", // 136-healthinfo-3.11
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(({ value }) => value === "ced") !==
      undefined,
    label: tk("informationForHealthcare.useOfForceContextCed.title"),
    requiredLabel: tk("informationForHealthcare.useOfForceContextCed.required"),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextCedOther", // 136-healthinfo-3.23
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextCed?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // AEP context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextAep", // 136-healthinfo-3.12
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(({ value }) => value === "aep") !==
      undefined,
    label: tk("informationForHealthcare.useOfForceContextAep.title"),
    requiredLabel: tk("informationForHealthcare.useOfForceContextAep.required"),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextAepOther", // 136-healthinfo-3.24
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextAep?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // Firearms context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextFirearms", // 136-healthinfo-3.13
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(({ value }) => value === "firearms") !==
      undefined,
    label: tk("informationForHealthcare.useOfForceContextFirearms.title"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextFirearms.required",
    ),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextFirearmsOther", // 136-healthinfo-3.25
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextFirearms?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  // Other/improvised context
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextOtherImprovised", // 136-healthinfo-3.14
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediatelyOnScene" as const,
          "enrouteToPlaceOfSafety" as const,
          "intermittentlyAtPlaceOfSafety" as const,
          "consistentlyAtPlaceOfSafety" as const,
          "detentionUntilHandover" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceType?.find(
        ({ value }) => value === "otherImprovised",
      ) !== undefined,
    label: tk(
      "informationForHealthcare.useOfForceContextOtherImprovised.title",
    ),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherImprovised.required",
    ),
    options: [
      {
        value: "immediatelyOnScene",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
        ),
      },
      {
        value: "enrouteToPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
        ),
      },
      {
        value: "intermittentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "consistentlyAtPlaceOfSafety",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
        ),
      },
      {
        value: "detentionUntilHandover",
        label: tk(
          "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.useOfForceContext.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "useOfForceContextOtherImprovisedOther", // 136-healthinfo-3.26
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.useOfForceContextOtherImprovised?.value === "other",
    label: tk("informationForHealthcare.useOfForceContextOther"),
    requiredLabel: tk(
      "informationForHealthcare.useOfForceContextOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  .withFieldAlwaysVisible({
    fieldName: "hasPersonBeenSearched", // 136-healthinfo-4.0
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("informationForHealthcare.hasPersonBeenSearched"),
    requiredLabel: tk("informationForHealthcare.hasPersonBeenSearchedRequired"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withReadonlyFieldAlwaysVisible({
    fieldName: "clothingRemovalWarning",
    type: "banner",
    bannerType: "info",
    body: tk("informationForHealthcare.clothingRemovalWarning"),
  })
  .withFieldConditionallyVisible({
    fieldName: "didSeizeProperty", // 136-healthinfo-4.1
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    isVisible({ formValues }) {
      return formValues.hasPersonBeenSearched?.value === "yes";
    },
    label: tk("informationForHealthcare.didSeizeProperty"),
    requiredLabel: tk("informationForHealthcare.didSeizePropertyRequired"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "detailsPropertySeized", // 136-healthinfo-4.2
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) => formValues.didSeizeProperty?.value === "yes",
    label: tk("informationForHealthcare.detailsPropertySeized"),
    requiredLabel: tk("informationForHealthcare.detailsPropertySeizedRequired"),
    multiline: true,
    rows: "5",
  })
  .withFieldAlwaysVisible({
    fieldName: "isUnderInfluence", // 136-healthinfo-5.0
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
    label: tk("informationForHealthcare.underInfluenceOfSubstance"),
    requiredLabel: tk(
      "informationForHealthcare.underInfluenceOfSubstanceRequired",
    ),
  })
  .withReadonlyFieldAlwaysVisible({
    fieldName: "substanceInfluenceBanner", // 136-healthinfo-5.1
    type: "banner",
    bannerType: "info",
    body: tk("informationForHealthcare.substanceInfluenceBanner"),
  })
  .withFieldAlwaysVisible({
    fieldName: "hasAdditionalRiskFactors", // 136-healthinfo-6.0
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("informationForHealthcare.hasAdditionalRiskFactors"),
    requiredLabel: tk(
      "informationForHealthcare.hasAdditionalRiskFactorsRequired",
    ),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "describeRiskFactors", // 136-healthinfo-6.1
    type: "multi-select",
    schema: z
      .array(valueAndLabelSchemaDeprecated(z.string()))
      .min(1, "At least one option must be selected."),
    isVisible: ({ formValues }) =>
      formValues.hasAdditionalRiskFactors?.value === "yes",
    label: tk("informationForHealthcare.describeRiskFactors.title"),
    requiredLabel: tk("informationForHealthcare.describeRiskFactors.required"),
    options: [
      {
        value: "substanceAbuse",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.substanceAbuse",
        ),
      },
      {
        value: "bereavement",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.bereavement",
        ),
      },
      {
        value: "bullying",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.bullying",
        ),
      },
      {
        value: "carerIssues",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.carerIssues",
        ),
      },
      {
        value: "clininicallyDiagnosedNeurodiverse",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.clininicallyDiagnosedNeurodiverse",
        ),
      },
      {
        value: "clininicallyDiagnosedMentalHealthIllness",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.clininicallyDiagnosedMentalHealthIllness",
        ),
      },
      {
        value: "clininicallyDiagnosedIllness",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.clininicallyDiagnosedIllness",
        ),
      },
      {
        value: "disability",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.disability",
        ),
      },
      {
        value: "domesticAbuse",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.domesticAbuse",
        ),
      },
      {
        value: "stress",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.stress",
        ),
      },
      {
        value: "financialIssues",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.financialIssues",
        ),
      },
      {
        value: "genderIdentityIssues",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.genderIdentityIssues",
        ),
      },
      {
        value: "traumaHistory",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.traumaHistory",
        ),
      },
      {
        value: "housingIssues",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.housingIssues",
        ),
      },
      {
        value: "militaryHistory",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.militaryHistory",
        ),
      },
      {
        value: "natalConcerns",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.natalConcerns",
        ),
      },
      {
        value: "previousConvictions",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.previousConvictions",
        ),
      },
      {
        value: "previouslySectioned",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.previouslySectioned",
        ),
      },
      {
        value: "relationshipIssues",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.relationshipIssues",
        ),
      },
      {
        value: "sexualOrientationIssues",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.sexualOrientationIssues",
        ),
      },
      {
        value: "suspectedSeriousCrime",
        label: tk(
          "informationForHealthcare.describeRiskFactors.options.suspectedSeriousCrime",
        ),
      },
      {
        value: "other",
        label: tk("informationForHealthcare.describeRiskFactors.options.other"),
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "describeRiskFactorsOther", // 136-healthinfo-6.2
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.describeRiskFactors?.find(({ value }) => value === "other") !==
      undefined,
    label: tk("informationForHealthcare.describeRiskFactorsOther"),
    requiredLabel: tk(
      "informationForHealthcare.describeRiskFactorsOtherRequired",
    ),
    multiline: true,
    rows: "5",
  })
  .withPage([
    "incidentDescription", // 136-healthinfo-1.0
  ])
  .withPage([
    "wasHealthcareProvided", // 136-healthinfo-2.0
    "medicalAttentionProvided", // 136-healthinfo-2.1
  ])
  .withPage([
    "wasForceUsed", // 136-healthinfo-3.0
    "useOfForceReminderBanner", // 136-healthinfo-3.1
  ])
  .withPage([
    "useOfForceType", // 136-healthinfo-3.2
  ])
  .withPage([
    "useOfForceContextHandcuffing", // 136-healthinfo-3.3
    "useOfForceContextHandcuffingOther", // 136-healthinfo-3.15
  ])
  .withPage([
    "useOfForceContextUnarmedSkills", // 136-healthinfo-3.4
    "useOfForceContextUnarmedSkillsOther", // 136-healthinfo-3.16
  ])
  .withPage([
    "useOfForceContextDogs", // 136-healthinfo-3.5
    "useOfForceContextDogsOther", // 136-healthinfo-3.17
  ])
  .withPage([
    "useOfForceContextBaton", // 136-healthinfo-3.6
    "useOfForceContextBatonOther", // 136-healthinfo-3.18
  ])
  .withPage([
    "useOfForceContextIrritantSpray", // 136-healthinfo-3.7
    "useOfForceContextIrritantSprayOther", // 136-healthinfo-3.19
  ])
  .withPage([
    "useOfForceContextLimbBodyRestraints", // 136-healthinfo-3.8
    "useOfForceContextLimbBodyRestraintsOther", // 136-healthinfo-3.20
  ])
  .withPage([
    "useOfForceContextSpitGuard", // 136-healthinfo-3.9
    "useOfForceContextSpitGuardOther", // 136-healthinfo-3.21
  ])
  .withPage([
    "useOfForceContextShield", // 136-healthinfo-3.10
    "useOfForceContextShieldOther", // 136-healthinfo-3.22
  ])
  .withPage([
    "useOfForceContextCed", // 136-healthinfo-3.11
    "useOfForceContextCedOther", // 136-healthinfo-3.23
  ])
  .withPage([
    "useOfForceContextAep", // 136-healthinfo-3.12
    "useOfForceContextAepOther", // 136-healthinfo-3.24
  ])
  .withPage([
    "useOfForceContextFirearms", // 136-healthinfo-3.13
    "useOfForceContextFirearmsOther", // 136-healthinfo-3.25
  ])
  .withPage([
    "useOfForceContextOtherImprovised", // 136-healthinfo-3.14
    "useOfForceContextOtherImprovisedOther", // 136-healthinfo-3.26
  ])
  .withPage([
    "hasPersonBeenSearched", // 136-healthinfo-4.0
    "clothingRemovalWarning",
    "didSeizeProperty", // 136-healthinfo-4.1
    "detailsPropertySeized", // 136-healthinfo-4.2
  ])
  .withPage([
    "isUnderInfluence", // 136-healthinfo-5.0
    "substanceInfluenceBanner", // 136-healthinfo-5.1
  ])
  .withPage([
    "hasAdditionalRiskFactors", // 136-healthinfo-6.0
    "describeRiskFactors", // 136-healthinfo-6.1
    "describeRiskFactorsOther", // 136-healthinfo-6.2
  ])
  .build({ t });

export type InformationForHealthcareFormData = ReturnType<
  typeof formSpecification
>["formType"];
